import { Controller } from '@hotwired/stimulus';
import frameDebounce from 'lib/frame-debounce';
import { GO_AHEAD_GET_STICKY } from 'lib/ez-custom-events';
import MediaMatcher from 'lib/media-matcher';

const mediaMatcher = new MediaMatcher();

declare global {
  interface Window {
    waitToGetSticky:boolean;
  }
}

export default class DynamicStickyController extends Controller {
  declare readonly isStickyClass:string;

  declare readonly showElementClass:string;

  declare readonly hideElementClass:string;

  declare readonly offsetElementSelectorValue:string;

  declare readonly waitToInitValue:boolean;

  declare readonly scrollOffsetValue:number;

  static classes = [
    'isSticky',
    'showElement',
    'hideElement',
  ];

  static values = {
    mobileOnly: { type: Number, default: 0 },
    waitToInit: { type: Boolean, default: false },
    offsetElementSelector: String,
    scrollOffset: { type: Number, default: 0 },
  }

  lastScrolledPosition = window.scrollY;

  isScrolled = false;

  connect() {
    if (this.waitToGetSticky()) {
      window.addEventListener(GO_AHEAD_GET_STICKY, this.setupSticky);
    } else {
      this.setupSticky();
    }

    window.addEventListener('resize', this.applyStickyOffset);
  }

  disconnect() {
    window.removeEventListener(GO_AHEAD_GET_STICKY, this.setupSticky);
    window.removeEventListener('resize', this.applyStickyOffset);
    window.removeEventListener('scroll', this.normalScrollCheck);
    window.removeEventListener('scroll', this.dynamicScrollCheck);
  }

  setupSticky = () => {
    this.setupStickyMediaQueries();

    this.initialState();

    window.addEventListener('scroll', this.normalScrollCheck);
  }

  setupStickyMediaQueries() {
    mediaMatcher.addMediaQuery('(max-width: 635px)',
      () => this.showStickyHeaderSearchBar(true),
      this.initialState);

    mediaMatcher.evaluateAll();
    mediaMatcher.addListeners();
  }

  initialState = () => {
    this.stickElement();
    this.showElement();

    if (this.isCloseToTop()) {
      this.hideStickyHeaderSearchBar();
    }

    window.addEventListener('scroll', this.dynamicScrollCheck);
  }

  disableDynamicSticky = () => {
    this.stickElement();
    this.showElement();
    window.removeEventListener('scroll', this.dynamicScrollCheck);
  }

  isScrollingUp = () => this.lastScrolledPosition > window.scrollY;

  isScrolledToTop = () => window.scrollY <= 0;

  isCloseToTop = (withThreshold = false) => {
    const headerEL = this.element as HTMLElement;
    const topThreshold = withThreshold ? 50 : 0;

    return window.scrollY <= (headerEL.offsetHeight + topThreshold);
  }

  hasScrolledPastLastStickyElement = () => {
    const stickyElementsQuery = document.querySelectorAll('[data-controller*="dynamic-sticky"]');
    const stickyElements = Array.from(stickyElementsQuery as NodeListOf<HTMLElement>);

    const lastStickyElement = stickyElements.reduce((prevEl, el) => {
      const prevElBottom = prevEl.offsetTop + prevEl.offsetHeight;
      const elBottom = el.offsetTop + el.offsetHeight;

      if (elBottom > prevElBottom) return el;

      return prevEl;
    });

    return window.scrollY > lastStickyElement.clientHeight;
  }

  showElement = () => {
    if (this.element.classList.contains(this.isStickyClass)) {
      this.element.classList.remove(this.hideElementClass);
      this.element.classList.add(this.showElementClass);

      if (this.mainSearchBar) {
        this.showStickyHeaderSearchBar();
      }

      this.applyStickyOffset();
    }
  }

  hideElement = () => {
    this.element.classList.remove(this.showElementClass);
    this.element.classList.add(this.hideElementClass);

    this.removeStickyOffset();
  }

  unstickElement = () => {
    this.element.classList.remove(this.showElementClass);
    this.element.classList.remove(this.isStickyClass);
  }

  stickElement = () => {
    this.element.classList.add(this.isStickyClass);
  }

  showStickyHeaderSearchBar = (mobile = false) => {
    if (!this.isCloseToTop(true) || mobile) {
      this.mainSearchBar.classList.add('is-visible');

      this.searchField.disabled = false;
    }
  }

  hideStickyHeaderSearchBar() {
    if (!this.mobileSearchButtonVisible() && document.body.classList.contains('main-search-soft-hidden') && this.mainSearchBar) {
      this.mainSearchBar.classList.remove('is-visible');

      this.searchField.disabled = true;
    }
  }

  mobileSearchButtonVisible() {
    const mobileSearchButton = document.querySelector('[data-action*="click->site-header#toggleMobileSearch"]') as HTMLElement;

    if (!mobileSearchButton) return false;

    return getComputedStyle(mobileSearchButton).display !== 'none';
  }

  applyStickyOffset = () => {
    if (this.offsetElementSelectorValue) {
      const offsetElement = document.querySelector(this.offsetElementSelectorValue) as HTMLElement;
      const thisElement = this.element as HTMLElement;

      thisElement.style.top = `${offsetElement.offsetHeight}px`;
    }
  }

  removeStickyOffset = () => {
    const thisElement = this.element as HTMLElement;
    thisElement.style.top = '0';
  }

  applyStickyHeader = () => {
    if (this.isScrollingUp()) {
      this.scrollingUpActions();
    } else {
      this.scrollingDownActions();
    }

    // store last scrolled position
    this.lastScrolledPosition = window.scrollY;
  }

  scrollingUpActions = () => {
    this.showElement();

    if (this.isScrolledToTop()) {
      this.unstickElement();
    }

    if (this.isCloseToTop()) {
      this.hideStickyHeaderSearchBar();
    }
  }

  scrollingDownActions = () => {
    if (this.hasScrolledPastLastStickyElement()) {
      this.stickElement();
      this.hideElement();
    }
  }

  checkScroll = () => {
    if (window.scrollY > this.scrollOffsetValue && this.isScrolled === false) {
      document.body.classList.add('is-scrolled');
      this.isScrolled = true;
    } else if (window.scrollY <= 0 && this.isScrolled === true) {
      document.body.classList.remove('is-scrolled');
      this.isScrolled = false;
    }
  }

  normalScrollCheck = frameDebounce(this.checkScroll);

  dynamicScrollCheck = frameDebounce(() => {
    this.applyStickyHeader();
  });

  waitToGetSticky() {
    return window.waitToGetSticky || this.waitToInitValue;
  }

  get mainSearchBar() {
    return document.querySelector('.ez-site-header__search-fullwidth') as HTMLElement;
  }

  get searchField() {
    return this.mainSearchBar.querySelector('.ez-search-form__input') as HTMLInputElement;
  }
}
